import "./appHeader.scss";
import { useEffect, useState, useContext, useMemo } from "react";
// router
import { useLocation, useNavigate, useParams, useNavigationType } from "react-router-dom";
import { Action } from "history";
// redux
import { useDispatch, useSelector } from "react-redux";
import { userType as userTypeData, UserData } from "@src/@slice/userSlice";
import { callCount as callCountData } from "@src/@slice/globalSlice";
// context
import { UserContext } from "../../../App";
// types
import { IMe } from "@src/api/common";
// utils
import { BASE_URL, HOSTNAME, isExternalUrl } from "@src/utils";
// api
import storage from "@src/api/storage";
import { axiosTokenErrorHandler } from "@src/api/axios";
import { increment, decrement, historyLen } from "../../../@slice/historySlice";
// components
import WeddinghallReminder from "../../nudgeModals/WeddinghallReminder";
import { CustomHeader } from "@shared/src";

/**
 * Appheader 컴포넌트
 */

// 액션 아이템 타입
type TActionItem =
  | "search"
  | "chat"
  | "push"
  | "like"
  | "close"
  | "check"
  | "back"
  | "stop"
  | "more";

// 헤더 타입
interface AppHeaderProps {
  type: "main" | "product" | "stack" | "nonStack";
  title?: string;
  titleColor?: string;
  titleStyle?: React.CSSProperties;
  actionItems?: {
    name: TActionItem;
    isIcon?: boolean;
    badge?: string | number | boolean;
    badgeType?: "text" | "icon" | "dot";
    callback?: () => void;
  }[];
  iconColor?: "black" | "white";
  backgroundColor?: string;
  scrollHide?: boolean;
  headerPostion?: "fixed" | "absolute" | "relative";
  linkItem?: { name: string; callback: () => void; color?: string };
}

const AppHeader = ({
  type,
  title,
  titleColor = "#000",
  titleStyle,
  actionItems,
  iconColor = "black",
  backgroundColor = "#fff",
  scrollHide = true,
  linkItem,
  headerPostion = "fixed",
}: AppHeaderProps) => {
  // user
  const user = useContext(UserContext);

  // redux
  const dispatch = useDispatch();

  // callcount
  const callCount = useSelector(callCountData);

  // navigate
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  // location
  const location = useLocation();
  const historyLength = useSelector(historyLen);

  // product id
  const { productId } = useParams<{ productId: string | undefined }>();

  // 히스토리 관련
  useEffect(() => {
    switch (navigationType) {
      case Action.Push:
        dispatch(increment());
        break;
      case Action.Pop:
        if (historyLength > 0) dispatch(decrement());
        break;
    }
  }, [location]);

  // 뒤로가기
  const goBack = (user: IMe) => {
    if (historyLength >= 1 || user.me === null) {
      navigate(-1);
    } else {
      window.location.href = "h2mwbell://webview/close";
    }
  };

  // 검색
  const linkSearch = () => {
    let link = isExternalUrl(`${HOSTNAME}/search`);
    if (window.location.pathname === `/product/${productId}`) {
      link = isExternalUrl(`${HOSTNAME}/search?productId=${productId}`);
    } else if (
      window.location.pathname === `/weddinghall/search` ||
      window.location.pathname === `/weddinghall/home` ||
      window.location.pathname === `/weddinghall/slot` ||
      window.location.pathname === `/weddinghall/event`
    ) {
      link = isExternalUrl(`${HOSTNAME}/weddinghall/search-filter?hidetop=true`);
    }
    window.location.href = link;
  };

  // 좋아요
  const linkLike = () => {
    let link = isExternalUrl(`${HOSTNAME}/my-interest/like`);
    window.location.href = link;
  };

  // 푸시 체크
  const checkPushTsp = async (pushTsp: string) => {
    if (!user.me) return true;
    const checkTime = await storage.getItem(user.me.uuid, "pushCheck");
    if (checkTime !== false) {
      return new Date(pushTsp) > new Date(checkTime);
    } else {
      return true;
    }
  };

  // 새로운 푸시 체크
  const [showNewPush, setShowNewPush] = useState(null);

  // 유저 데이터
  useEffect(() => {
    (async () => {
      setShowNewPush(await checkPushTsp(user.newPushTsp));
    })();
  }, [user?.newPushTsp, user?.me?.weddingTsp, callCount]);

  // 유저 데이터
  const userType = useSelector(userTypeData);

  // 추가정보 업데이트 알림 노출 여부
  const [showInfoUpdate, setShowInfoUpdate] = useState(false);

  const actionItemsFinal = useMemo(() => {
    return actionItems
      ? actionItems.map((item) => {
          const newItem = { ...item, isIcon: true };
          if (item.name !== "more") {
            if (userType === "GUEST_USER") {
              newItem.callback = () => {
                const checkAccessToken = localStorage.getItem("accessToken");
                if (checkAccessToken) {
                  // console.log("회원 상태 완료안됨");
                }
                axiosTokenErrorHandler("confirm", 0);
              };
            } else if (userType === "COMPLETE_AUTH_USER") {
              switch (item.name) {
                case "push":
                  newItem.badgeType = "dot";
                  if (user?.updated && showNewPush) {
                    newItem.badge = true;
                  } else {
                    newItem.badge = false;
                  }
                  newItem.callback = () => {
                    if (user.me) {
                      storage.setItem(user.me.uuid, "pushCheck", new Date().toString());
                    }
                    window.location.href = "h2mwbell://push";
                  };
                  break;
                case "chat":
                  newItem.callback = () => {
                    window.location.href = "h2mwbell://chat";
                  };
                  break;
                case "search":
                  newItem.callback = () => {
                    linkSearch();
                  };
                  break;
                case "like":
                  newItem.callback = () => {
                    linkLike();
                  };
                  break;
                case "close":
                  newItem.callback = () => {
                    window.location.href = "h2mwbell://webview/close";
                  };
                  break;
                case "check":
                  break;
                case "stop":
                  // window.location.href = `${window.location.origin}/booking/real-time/stop-reservation`;
                  break;
              }
            }
          } else {
            if (user?.updated) {
              newItem.badge = user.bookieUnreadCount > 0;
            }
            newItem.callback = () => {
              window.location.href = isExternalUrl(`${HOSTNAME}/mypage?hidetop=true`);
            };
          }
          return newItem;
        })
      : null;
  }, [userType, actionItems, user, showNewPush]);

  return (
    <>
      <CustomHeader
        id="AppHeaderWrap"
        className={`${showInfoUpdate ? "info-update-show" : ""} app-header-wrap`}
        scrollHide={scrollHide}
        title={
          type === "main" ? (
            <>
              {user?.me && user?.me?.weddingTsp ? (
                <span
                  onClick={() =>
                    (window.location.href = isExternalUrl(
                      `${HOSTNAME}/sign-additional/2?type=Home_Dday_Complete`,
                    ))
                  }
                  className="d-day"
                >
                  D{user.dDay > 0 ? "+" : "-"}
                  {Math.abs(user.dDay)}
                </span>
              ) : (
                <>
                  <span
                    className="d-day unknown"
                    onClick={() => {
                      const checkAccessToken = localStorage.getItem("accessToken");
                      if (!user?.me?.uuid) {
                        axiosTokenErrorHandler("confirm", 0);
                        return;
                      }
                      !showInfoUpdate
                        ? (window.location.href = isExternalUrl(
                            `${HOSTNAME}/sign-additional/2?type=Home_Dday_Complete`,
                          ))
                        : setShowInfoUpdate(false);
                    }}
                  >
                    미정
                  </span>
                </>
              )}
            </>
          ) : (
            title
          )
        }
        titleColor={titleColor}
        titleStyle={
          type === "main"
            ? {
                color: user?.me && user?.me?.weddingTsp ? "#191919" : "#c8c8c8",
                fontSize: "34px",
                fontWeight: 700,
                lineHeight: "50px",
              }
            : titleStyle
        }
        iconColor={iconColor}
        backgroundColor={backgroundColor}
        headerPostion={headerPostion}
        actionItems={actionItemsFinal}
        linkItem={linkItem}
        goBack={type === "product" || type === "stack" ? () => goBack(user) : undefined}
      />
      {/* 타입이 메인일때 && 정보 업데이트 필요시 아래 알림창 노출 */}
      {type === "main" && (
        <WeddinghallReminder
          onShow={(state: boolean) => {
            setShowInfoUpdate(state);
          }}
        />
      )}
    </>
  );
};

export default AppHeader;
